import { SupportedStorage } from "@supabase/supabase-js";

/**
 * Custom cookie storage for Supabase auth to enable cross-subdomain SSO.
 *
 * Sets cookies with domain=.projectassistant.ai so sessions are shared across:
 * - localdocs.projectassistant.ai
 * - nexus.projectassistant.ai
 * - Any other *.projectassistant.ai subdomain
 *
 * This enables true SSO: sign in on one subdomain, automatically signed in on others.
 *
 * CHUNKING: Large sessions are split across multiple cookies to avoid 4KB browser limit.
 * Format: sb-auth-token-0, sb-auth-token-1, sb-auth-token-2, etc.
 */

const COOKIE_OPTIONS = {
  domain: ".projectassistant.ai",
  path: "/",
  sameSite: "lax" as const,
  secure: true, // HTTPS only in production
};

// Maximum size for each cookie chunk (3KB to leave room for encoding overhead)
const CHUNK_SIZE = 3000;

function getCookie(name: string): string | null {
  // Check if this is a chunked cookie by looking for chunk 0
  const chunk0 = getSimpleCookie(`${name}-0`);

  if (chunk0) {
    // Reassemble chunked cookie
    let fullValue = chunk0;
    let chunkIndex = 1;

    while (true) {
      const chunk = getSimpleCookie(`${name}-${chunkIndex}`);
      if (!chunk) break;
      fullValue += chunk;
      chunkIndex++;
    }

    try {
      return decodeURIComponent(fullValue);
    } catch (e) {
      console.error("[CookieStorage] Failed to decode chunked cookie:", e);
      return null;
    }
  }

  // Fall back to non-chunked cookie (for backwards compatibility)
  return getSimpleCookie(name);
}

function getSimpleCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const encoded = parts.pop()?.split(";").shift() || null;
    if (encoded) {
      try {
        return decodeURIComponent(encoded);
      } catch (e) {
        // Return raw value if decoding fails (for chunk assembly)
        return encoded;
      }
    }
  }
  return null;
}

function setCookie(name: string, value: string, maxAge?: number): void {
  const isProduction = window.location.hostname.includes("projectassistant.ai");

  // URL-encode the value
  const encodedValue = encodeURIComponent(value);

  // Check if we need to chunk the cookie
  if (encodedValue.length > CHUNK_SIZE) {
    // Clear any existing chunks first
    clearChunkedCookie(name);

    // Split into chunks
    const chunks: string[] = [];
    for (let i = 0; i < encodedValue.length; i += CHUNK_SIZE) {
      chunks.push(encodedValue.substring(i, i + CHUNK_SIZE));
    }

    // Set each chunk
    chunks.forEach((chunk, index) => {
      setSimpleCookie(`${name}-${index}`, chunk, maxAge, isProduction);
    });
  } else {
    // Cookie is small enough, set normally
    setSimpleCookie(name, encodedValue, maxAge, isProduction);
  }
}

function setSimpleCookie(
  name: string,
  encodedValue: string,
  maxAge: number | undefined,
  isProduction: boolean
): void {
  let cookie = `${name}=${encodedValue}; path=${COOKIE_OPTIONS.path}; sameSite=${COOKIE_OPTIONS.sameSite}`;

  if (isProduction) {
    cookie += `; domain=${COOKIE_OPTIONS.domain}; secure`;
  }

  if (maxAge) {
    cookie += `; max-age=${maxAge}`;
  }

  document.cookie = cookie;
}

function removeCookie(name: string): void {
  const isProduction = window.location.hostname.includes("projectassistant.ai");

  // Remove main cookie
  removeSimpleCookie(name, isProduction);

  // Remove chunked cookies if they exist
  clearChunkedCookie(name);
}

function removeSimpleCookie(name: string, isProduction: boolean): void {
  let cookie = `${name}=; path=${COOKIE_OPTIONS.path}; max-age=0; sameSite=${COOKIE_OPTIONS.sameSite}`;
  if (isProduction) {
    cookie += `; domain=${COOKIE_OPTIONS.domain}; secure`;
  }
  document.cookie = cookie;

  // Also remove for current hostname as fallback
  document.cookie = `${name}=; path=/; max-age=0`;
}

function clearChunkedCookie(name: string): void {
  const isProduction = window.location.hostname.includes("projectassistant.ai");

  // Try to remove up to 10 chunks (should be more than enough)
  for (let i = 0; i < 10; i++) {
    const chunkName = `${name}-${i}`;
    if (getSimpleCookie(chunkName)) {
      removeSimpleCookie(chunkName, isProduction);
    }
  }
}

export const cookieStorage: SupportedStorage = {
  getItem: (key: string): string | null => {
    return getCookie(key);
  },

  setItem: (key: string, value: string): void => {
    // Set cookie with 30 day expiration (same as Supabase default)
    setCookie(key, value, 30 * 24 * 60 * 60);
  },

  removeItem: (key: string): void => {
    removeCookie(key);
  },
};
